import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { regions } from "@/data/regions";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";

export const Regions = () => {
  useSeo({
    title: "International Buyer Guides — Sourcing from India by Country",
    description:
      "Regional sourcing guides for international buyers importing home décor, furniture, textile and handicrafts from India. USA · UK · Germany · France · Netherlands · Belgium · Sweden · Denmark · Italy · Spain · Middle East (GCC) · Australia · New Zealand · South Africa. Compliance, ports, freight, MOQ.",
    path: "/regions",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ItemList",
          "@id": `${SITE_URL}/regions#list`,
          name: "Asia Sourcing — regional buyer guides",
          numberOfItems: regions.length,
          itemListElement: regions.map((r, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: `${r.country} — India sourcing`,
            url: `${SITE_URL}/regions/${r.id}`,
          })),
        },
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Regions", path: "/regions" },
        ]),
      ],
    },
    jsonLdId: "regions-hub-jsonld",
  });

  return (
    <div data-testid="regions-page">
      <header className="wrap" style={{ paddingBlock: "calc(74px + var(--section)) 3rem" }}>
        <Reveal>
          <p className="eyebrow" data-testid="regions-eyebrow">
            International buyers · By country
          </p>
        </Reveal>
        <WordReveal
          as="h1"
          text="Sourcing from India — by country."
          className="display"
          stagger={70}
          testId="regions-h1"
        />
        <Reveal
          as="p"
          delay={200}
          style={{
            maxWidth: "72ch",
            color: "var(--ink-soft)",
            fontSize: "1.12rem",
            marginTop: "1.4rem",
            lineHeight: 1.65,
          }}
        >
          Country-specific operational playbooks for international buyers importing from India — home décor, furniture, textile, brass metalware, handicrafts. Every guide covers destination-market compliance (FDA / Prop-65 / UKCA / CE / LFGB / REACH), discharge ports and freight, payment terms, and category-fit recommendations from our supplier network across 15 Indian manufacturing clusters.
        </Reveal>
      </header>

      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        data-testid="regions-grid-section"
      >
        <Reveal as="p" className="eyebrow">Regional guides</Reveal>
        <Reveal as="h2" className="display" style={{ margin: "1rem 0 2rem" }}>
          Fifteen export markets we run programmes into.
        </Reveal>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1.4rem",
          }}
        >
          {regions.map((r) => (
            <Reveal key={r.id}>
              <Link
                to={`/regions/${r.id}`}
                style={{
                  display: "block",
                  padding: "1.6rem 1.4rem",
                  border: "1px solid var(--line)",
                  textDecoration: "none",
                  color: "inherit",
                  height: "100%",
                }}
                data-testid={`region-tile-${r.id}`}
              >
                <p
                  className="mono"
                  style={{
                    color: "var(--brass-deep)",
                    fontSize: ".64rem",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    margin: 0,
                  }}
                >
                  <span aria-hidden="true" style={{ fontSize: "1.4em", marginRight: ".4rem", verticalAlign: "middle" }}>
                    {r.flag}
                  </span>
                  {r.name}
                </p>
                <p
                  style={{
                    fontFamily: "var(--display)",
                    fontSize: "1.25rem",
                    margin: ".55rem 0 .55rem",
                    letterSpacing: "-.005em",
                    lineHeight: 1.25,
                  }}
                >
                  Sourcing from India for {r.country} buyers
                </p>
                <p
                  style={{
                    color: "var(--ink-soft)",
                    fontSize: ".9rem",
                    margin: ".3rem 0 .8rem",
                    lineHeight: 1.55,
                  }}
                >
                  Ports: {(r.ports || []).slice(0, 3).join(" · ")}
                </p>
                <p
                  className="mono"
                  style={{
                    fontSize: ".68rem",
                    color: "var(--brass-deep)",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    margin: 0,
                  }}
                >
                  Read regional guide →
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
      >
        <Reveal as="p" className="eyebrow">Not sure where to start?</Reveal>
        <Reveal as="h2" className="display">
          Send a country-specific sourcing brief.
        </Reveal>
        <Reveal
          as="div"
          style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "2rem" }}
        >
          <Link
            to="/contact"
            className="btn btn-fill btn-lg btn-glow"
            data-testid="regions-cta-contact"
          >
            Start an enquiry
          </Link>
          <Link to="/services" className="btn btn-lg" data-testid="regions-cta-services">
            See our services
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Regions;
